import React, { useEffect, useState } from 'react'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone';
import './addBordingPassCount.css'
import CloseIcon from '@mui/icons-material/Close';
import { FormControl, TextField } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import swal from 'sweetalert';
import { ButtonList } from 'sweetalert/typings/modules/options/buttons';
import ButtonWrapper from '../../widgets/button/button';
import { createBPCount, getLockDateRange } from '../../api-services/employee-services';
import BpCountSummary from '../bpCountSummary/bpCountSummary';
import { SnackbarService } from '../../services/snackBehaviorSubject/snackBehaviorSubject';
dayjs.extend(utc);
dayjs.extend(timezone);

interface BpCountSummaryValues {
    clockIn?: string;
    clockOut?: string;
    workingHours?: string;
    clockInDate?: string;
}

interface AddBordingPassCountProps {
    onClose: () => void;
    title: string;
    onAdded?: () => void;
    summary?: BpCountSummaryValues;
}

const AddBordingPassCount = (props: AddBordingPassCountProps) => {
    const { control, handleSubmit } = useForm();
    const snackbarService = SnackbarService()
    const [date, setDate] = useState<string>('')
    const [count, setCount] = useState<string>('')
    const [lockDateRange, setLockDateRange] = useState<{ fromDate: string; toDate: string } | null>(null);

    const fetchLoackDateRange = () => {
        getLockDateRange().then((response: any) => {
            if (response?.status === 200) {
                setLockDateRange(response.data);
            }
        }).catch((error: any) => {
            console.error('Error fetching lock date range:', error);
        });
    }

    useEffect(() => {
        fetchLoackDateRange();
    }, [])

    const handleDateChange = (value: Dayjs | null, onChange: (value: string) => void) => {
        if (!value) {
            setDate('');
            onChange('');
            return;
        }

        const now = dayjs();

        const combined = value
            .hour(now.hour())
            .minute(now.minute())
            .second(now.second())
            .millisecond(now.millisecond());
        const formatted = combined.format();
        setDate(formatted);
        onChange(formatted);
    };

    const handleCountChange = (e: any) => {
        setCount(e.target.value)
    }

    const onSubmit = () => {
        const selectedDate = date ? dayjs(date).format('DD/MM/YYYY') : ''
        const isSameAsSummaryDate = props.summary?.clockInDate && selectedDate === props.summary.clockInDate
        const notClockedOut = !props.summary?.clockOut

        if (isSameAsSummaryDate && notClockedOut) {
            snackbarService.next({ data: { open: true, severity: 'error', message: 'Before submit the count please clock out' } })
            return
        }

        swal({
            title: 'Are you sure?',
            text: 'Do you want to save this count?',
            icon: 'warning',
            buttons: true as unknown as ButtonList | (string | boolean)[] | undefined,
        }).then((confirmed) => {
            if (!confirmed) {
                return
            }

            const payload: { date: string; count: number } = {
                date: date,
                count: Number(count)
            }
            createBPCount(payload).then((response: any) => {
                if (response?.status === 201) {
                    snackbarService.next({ data: { open: true, severity: 'success', message: response.data.message } })
                    if (props.onAdded) {
                        props.onAdded()
                    }
                    props.onClose()
                } else {
                    snackbarService.next({ data: { open: true, severity: 'error', message: response?.data.message || 'Failed to save count' } })
                }
            });
        })
    }



    return (
        <div className='bording-pass-count-container'>
            <div className="modal-heading">
                <h4>{props.title}</h4>
                <CloseIcon
                    className='modal-close-button'
                    onClick={props.onClose}
                />
            </div>
            <div className="modal-body">
                <span className='bp-count-tip'>* Only added your working hour count, not overtime hour count</span>
                <form onSubmit={handleSubmit(onSubmit)} className='bp-count-form'>
                    <div className='bp-count-scroll-area'>
                        {props.summary ? <BpCountSummary {...props.summary} /> : null}
                        <FormControl fullWidth>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DemoContainer components={['DatePicker']}>
                                    <Controller
                                        name="date"
                                        control={control}
                                        rules={{ required: 'Date is required' }}
                                        render={({ field, fieldState }) => (
                                            <DatePicker
                                                label='Date'
                                                format='DD/MM/YYYY'
                                                disableFuture
                                                value={date ? dayjs(date) : null}
                                                onChange={(value) => handleDateChange(value, field.onChange)}
                                                shouldDisableDate={(date) => {
                                                    if (!lockDateRange) {
                                                        return false; // If lockDateRange is not available, don't disable any dates
                                                    }
                                                    return !date.isBefore(dayjs(lockDateRange.fromDate, 'YYYY-MM-DD')) && !date.isAfter(dayjs(lockDateRange.toDate, 'YYYY-MM-DD'));
                                                }}
                                                slotProps={{
                                                    textField: {
                                                        required: true,
                                                        error: !!fieldState.error,
                                                        helperText: fieldState.error?.message
                                                    }
                                                }}
                                                sx={{ width: '100%' }}
                                            />
                                        )}
                                    />
                                </DemoContainer>
                            </LocalizationProvider>
                        </FormControl>
                        <FormControl sx={{ mt: 1 }} fullWidth>
                            <Controller
                                name="count"
                                control={control}
                                render={() => (
                                    <TextField
                                        label='Count'
                                        value={count}
                                        onChange={handleCountChange}
                                        required
                                    />
                                )}
                            />
                        </FormControl>
                    </div>
                    <div className='leave-application-button-container'>
                        <ButtonWrapper title='submit' id='submit-button' variant='contained' type='submit' />
                    </div>
                </form>
            </div>
        </div>
    )
}

export default AddBordingPassCount
